import { getCurrentAuthUser } from "../../repositories/authRepository";
import * as matchRepository from "../../repositories/matchRepository";
import * as matchAccessRepository from "../../repositories/matchAccessRepository";
import { patchMatchFields } from "./matchDocumentHelpers";
import { tryMigrateMatchCredentials } from "./matchAccessMigration";

function isPermissionDenied(err) {
  const code = err?.code || "";
  return code === "permission-denied" || code === "PERMISSION_DENIED";
}

function finalizeJoinRole(match, uid, role, opts = {}) {
  if (opts.spectatorOnly) return role;
  if (uid && match?.createdBy === uid && role === "spectator") {
    return "referee";
  }
  return role;
}

function joinOk(match, uid, role, opts = {}) {
  return { ok: true, role: finalizeJoinRole(match, uid, role, opts) };
}

async function completeRefereeJoin(matchId, match, uid) {
  if (match.createdBy !== uid && match.deputyRefereeUid !== uid) {
    try {
      await patchMatchFields(matchId, { deputyRefereeUid: uid });
    } catch (e) {
      if (isPermissionDenied(e)) {
        return { ok: false, reason: "rules" };
      }
      throw e;
    }
  }
  return { ok: true, role: "referee" };
}

/**
 * After first join, access grants are stored without accessCode (rules strip it).
 * Re-join must not rewrite the grant — Firestore only allows create + strip updates.
 */
async function resolveExistingGrantJoin(matchId, match, uid, opts = {}, accessCode = "") {
  const grant = await matchAccessRepository.fetchAccessGrant(matchId, uid);
  const code = (accessCode ?? "").trim();
  if (opts.spectatorOnly) {
    if (grant?.role === "spectator") {
      return joinOk(match, uid, "spectator", opts);
    }
    return null;
  }
  if (grant?.role === "referee") {
    if (match.createdBy === uid || match.deputyRefereeUid === uid) {
      return joinOk(match, uid, "referee", opts);
    }
    if (!code) {
      return joinOk(match, uid, "spectator", opts);
    }
    return null;
  }
  if (grant?.role === "spectator" && !code) {
    return joinOk(match, uid, "spectator", opts);
  }
  return null;
}

async function tryRefereeGrant(matchId, accessCode) {
  const uid = getCurrentAuthUser()?.uid;
  if (!uid) return false;
  try {
    await matchAccessRepository.createAccessGrant(matchId, uid, {
      role: "referee",
      accessCode,
    });
    try {
      await matchAccessRepository.stripAccessCodeFromGrant(matchId, uid);
    } catch {
      /* non-fatal */
    }
    return true;
  } catch (e) {
    if (isPermissionDenied(e)) return false;
    throw e;
  }
}

async function trySpectatorGrant(matchId, accessCode, { openRoom }) {
  const uid = getCurrentAuthUser()?.uid;
  if (!uid) return false;
  try {
    if (openRoom) {
      await matchAccessRepository.createAccessGrant(matchId, uid, {
        role: "spectator",
      });
    } else {
      await matchAccessRepository.createAccessGrant(matchId, uid, {
        role: "spectator",
        accessCode,
      });
    }
    try {
      await matchAccessRepository.stripAccessCodeFromGrant(matchId, uid);
    } catch {
      /* grant may not have accessCode */
    }
    return true;
  } catch (e) {
    if (isPermissionDenied(e)) return false;
    throw e;
  }
}

/**
 * Server-rules verification of room access (no credential reads).
 * @param {{ spectatorOnly?: boolean }} [opts] Audience share links — never elevate to referee.
 * @returns {Promise<{ ok: true, role: 'referee'|'spectator' } | { ok: false, reason: string }>}
 */
export async function verifyRoomJoin(matchId, accessCodeInput, opts = {}) {
  const spectatorOnly = opts.spectatorOnly === true;
  const uid = getCurrentAuthUser()?.uid;
  if (!uid) {
    return { ok: false, reason: "auth_required" };
  }

  let match = await matchRepository.fetchMatch(matchId);
  if (!match) {
    return { ok: false, reason: "not_found" };
  }

  match = await tryMigrateMatchCredentials(matchId, match);

  if (match.archivedAt) {
    return { ok: false, reason: "archived" };
  }

  const code = (accessCodeInput ?? "").trim();

  const existingGrant = await resolveExistingGrantJoin(
    matchId,
    match,
    uid,
    opts,
    code
  );
  if (existingGrant) {
    return existingGrant;
  }

  if (spectatorOnly) {
    if (code && (await trySpectatorGrant(matchId, code, { openRoom: false }))) {
      return joinOk(match, uid, "spectator", opts);
    }
    if (await trySpectatorGrant(matchId, "", { openRoom: true })) {
      return joinOk(match, uid, "spectator", opts);
    }
    return { ok: false, reason: "wrong_code" };
  }

  const mayUseRefCode =
    match.createdBy === uid || match.deputyRefereeUid === uid;

  if (code && mayUseRefCode && (await tryRefereeGrant(matchId, code))) {
    return joinOk(match, uid, "referee", opts);
  }

  if (code && (await trySpectatorGrant(matchId, code, { openRoom: false }))) {
    return joinOk(match, uid, "spectator", opts);
  }

  if (!code && match.hasViewCode) {
    return { ok: false, reason: "spectator_code_required" };
  }

  if (await trySpectatorGrant(matchId, "", { openRoom: true })) {
    return joinOk(match, uid, "spectator", opts);
  }

  return { ok: false, reason: "wrong_code" };
}

/**
 * @deprecated Use verifyRoomJoin; kept for callers that already verified.
 */
export async function claimDeputyRefereeAfterGrant(matchId) {
  const uid = getCurrentAuthUser()?.uid;
  if (!uid) return false;
  const match = await matchRepository.fetchMatch(matchId);
  if (!match) return false;
  if (match.createdBy === uid || match.deputyRefereeUid === uid) return true;
  const grant = await matchAccessRepository.fetchAccessGrant(matchId, uid);
  if (grant?.role !== "referee") return false;
  await patchMatchFields(matchId, { deputyRefereeUid: uid });
  return true;
}
